import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { anunciosMetaApi, mensagemDeErro } from "../api";
import { atualizarAvisosMeta } from "../avisos/avisosMeta";

// Uma chave por abertura do assistente: clique duplo no Publicar, ou repetir
// depois de a rede cair, devolve a MESMA campanha — o servidor nunca cria duas.
const novaChave = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);
const esperar = (ms) => new Promise((ok) => setTimeout(ok, ms));
const TENTATIVAS = 12;          // ~40 s esperando uma publicação que já está em andamento
const ESPERA_MS = 3500;

const FORM_INICIAL = {
  produtoId: null, propria: null, texto: "", titulo: "", botao: "ORDER_NOW",
  raioKm: 4, semanal: "100", semanas: 2, continua: false, horarioModo: "funcionamento",
};

export const centavos = (reaisTexto) => Math.round((Number(reaisTexto) || 0) * 100);

/**
 * Estado do assistente de impulsionar. Com `campanhaId` vira "novo anúncio
 * numa campanha que já existe": só a imagem e o texto são usados.
 */
export default function useImpulsionar(campanhaId) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [alterado, setAlterado] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const chave = useRef(novaChave());
  const textoMexido = useRef(false);     // a loja escreveu: a sugestão do produto não passa por cima

  useEffect(() => {
    let vivo = true;
    anunciosMetaApi.dadosDoAssistente().then((d) => {
      if (!vivo) return;
      setDados(d);
      setForm((f) => ({
        ...f,
        raioKm: Number(d.local?.raio_km || 4),
        semanal: String((d.sugerido_semanal_centavos || 10000) / 100),
        horarioModo: d.horario?.tem ? "funcionamento" : "dia_todo",
      }));
    }).catch(() => vivo && setErro("Não foi possível abrir o assistente. Tente de novo."));
    return () => { vivo = false; };
  }, []);

  const definir = useCallback((campo, valor) => {
    if (campo === "texto" || campo === "titulo") textoMexido.current = true;
    setAlterado(true);
    setForm((f) => ({ ...f, [campo]: valor }));
  }, []);

  const produto = useMemo(() => (dados?.produtos || []).find((p) => p.id === form.produtoId) || null, [dados, form.produtoId]);

  const escolherProduto = useCallback((p) => {
    setAlterado(true);
    setForm((f) => ({
      ...f, produtoId: p.id, propria: null,
      ...(textoMexido.current ? {} : { texto: p.sugestao.texto, titulo: p.sugestao.titulo }),
    }));
  }, []);

  const usarSugestao = useCallback(() => {
    if (!produto) return;
    textoMexido.current = false;
    setForm((f) => ({ ...f, texto: produto.sugestao.texto, titulo: produto.sugestao.titulo }));
  }, [produto]);

  const enviarImagem = useCallback(async (arquivo) => {
    if (!arquivo) return;
    setEnviandoImagem(true);
    try {
      const r = await anunciosMetaApi.enviarImagem(arquivo);
      setAlterado(true);
      setForm((f) => ({ ...f, propria: r }));
      if (r.aviso) toast.warning(r.aviso);
    } catch (e) { toast.error(mensagemDeErro(e, "Não foi possível enviar a imagem.")); }
    finally { setEnviandoImagem(false); }
  }, []);

  const imagem = form.propria?.imagem || produto?.foto || null;
  const semanalCentavos = centavos(form.semanal);
  const minimo = dados?.minimo_semanal_centavos || 0;
  const limites = dados?.limites || { texto: 600, titulo: 60, semanas: 12 };

  // O primeiro problema aparece ao lado do botão; sem problema, pode publicar.
  const problema = useMemo(() => {
    if (!dados) return "Carregando…";
    if (dados.pode_gerir === false) return "Você não tem permissão para publicar campanhas. Peça ao dono da loja a permissão Impulsione.";
    if (!campanhaId && dados.bloqueios?.length) return dados.bloqueios[0].texto;
    if (!imagem) return "Escolha um produto com foto ou envie uma imagem.";
    if (!form.texto.trim()) return "Escreva o texto do anúncio.";
    if (form.texto.length > limites.texto) return `O texto passou de ${limites.texto} caracteres.`;
    if (!form.titulo.trim()) return "Escreva o título do anúncio.";
    if (form.titulo.length > limites.titulo) return `O título passou de ${limites.titulo} caracteres.`;
    if (campanhaId) return null;
    if (semanalCentavos <= 0) return "Informe quanto investir por semana.";
    if (minimo && semanalCentavos < minimo) return `A Meta exige pelo menos R$ ${(minimo / 100).toFixed(2).replace(".", ",")} por semana nesta conta.`;
    return null;
  }, [dados, campanhaId, imagem, form.texto, form.titulo, limites.texto, limites.titulo, semanalCentavos, minimo]);

  const anuncio = () => ({
    produto_id: form.produtoId, imagem: form.propria?.imagem || null,
    texto: form.texto.trim(), titulo: form.titulo.trim(), botao: form.botao,
  });

  /** Publica. Devolve o cartão da campanha, ou null se não deu (o motivo já foi para o toast). */
  const publicar = useCallback(async () => {
    if (problema || publicando) return null;
    setPublicando(true);
    try {
      if (campanhaId) {
        const r = await anunciosMetaApi.novoAnuncio(campanhaId, anuncio());
        toast.success("Anúncio criado. A Meta analisa em até 24 horas.");
        return r.campanha;
      }
      const pedido = {
        ...anuncio(), chave_envio: chave.current, raio_km: form.raioKm, orcamento_semanal_centavos: semanalCentavos,
        semanas: form.continua ? null : form.semanas, continua: form.continua, horario_modo: form.horarioModo,
      };
      for (let i = 0; i < TENTATIVAS; i += 1) {
        try {
          const resp = await anunciosMetaApi.publicar(pedido);
          if (resp.status === 202) { await esperar(ESPERA_MS); continue; }       // a primeira chamada ainda está publicando
          toast.success("Campanha publicada. A Meta analisa em até 24 horas e avisamos no sino.");
          atualizarAvisosMeta();
          return resp.data.campanha;
        } catch (e) {
          if (e?.response) throw e;                                               // o servidor respondeu: erro de verdade
          await esperar(ESPERA_MS);                                               // rede caiu: repete com a MESMA chave
        }
      }
      toast.error("A publicação está demorando. Veja em Anúncios Meta se a campanha já aparece antes de tentar de novo.");
      return null;
    } catch (e) {
      chave.current = novaChave();   // erro definitivo: a próxima tentativa (corrigida) é um envio novo
      toast.error(mensagemDeErro(e, "Não foi possível publicar."));
      return null;
    } finally { setPublicando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problema, publicando, campanhaId, form, semanalCentavos]);

  return {
    dados, erro, form, definir, alterado, produto, imagem, escolherProduto, usarSugestao,
    enviarImagem, enviandoImagem, removerPropria: () => definir("propria", null),
    semanalCentavos, minimo, limites, problema, publicar, publicando,
  };
}
